import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Select, { components, MultiValue } from "react-select";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, DeleteIconButton, CancelIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import {
  ExportAsExcel,
} from "@siamf/react-export";

type FilterOption = { value: string; label: string };

const PAYMENT_STATUS_OPTIONS: FilterOption[] = [
  { value: "Paid", label: "Paid" },
  { value: "Pending", label: "Pending" },
  { value: "Partial Payment", label: "Partially paid" },
];

const INVOICE_STATUS_OPTIONS: FilterOption[] = [
  { value: "Draft", label: "Draft" },
  { value: "Finalized", label: "Finalized" },
  { value: "Cancelled", label: "Cancelled" },
];

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

// Define the type for batch payment report data
interface BatchPaymentReport {
  _id: string;
  bill_from: string;
  bill_to: string;
  payment_date: string | null;
  payment_status?: string;
  invoice_status?: string;
  date_range: string;
  batch_payment_id: string;
  report_date?: string;
  report_id?: string;
  report_document_id?: string;
  report_paid_amount?: number;
}

interface ExportRow {
  "Batch Payment ID": string;
  "Batch Invoice ID": string;
  "Bill From": string;
  "Bill To": string;
  "Payment Date": string;
  "Payment Status": string;
  "Invoice Status": string;
  "Report Date": string;
  "Date Range": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<BatchPaymentReport>();

const SignageBatchPayments = () => {
  const [reports, setReports] = useState<BatchPaymentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.batch_payments
  );
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detect login type and set base route (check more specific routes first)
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const isSignage = location.pathname.includes('/signage') && !isSignageTeammember;
  const isRealtor = location.pathname.includes('/realtor');
  const isTeammember = location.pathname.includes('/teammember') && !isRealtor;
  const isAgent = location.pathname.includes('/agent');
  
  const baseRoute = isSignageTeammember ? '/signageteammember' :
                   isSignage ? '/signage' : 
                   isRealtor ? '/realtor' :
                   isTeammember ? '/teammember' :
                   isAgent ? '/agent' : '/signage';
  const showSignageBatchActions = isSignage || isSignageTeammember;
  // Signage, realtor and agent logins always see it; team members need the create permission.
  const isTeamMemberLogin = isSignageTeammember || isTeammember;
  const showNewPaymentBatch = isTeamMemberLogin ? canCreate : true;

  const [searchInput, setSearchInput] = useState<string>("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<FilterOption[]>([]);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<FilterOption[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<{
    payment_status: string[];
    invoice_status: string[];
  }>({ payment_status: [], invoice_status: [] });
  const topRef = useRef<HTMLDivElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Format date helper — null/empty/invalid dates return ""
  function formatDate(dateString: string | null | undefined): string {
    if (!dateString || dateString === "N/A") return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Format currency helper
  function formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return "";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  // Fetch batch payment reports
  const fetchBatchPaymentReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/allocation/${userId}/list`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            payment_status: appliedFilters.payment_status,
            invoice_status: appliedFilters.invoice_status,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch batch payment reports");
      }

      const result = await response.json();
      console.log("Batch payment reports data:", result);
      
      setRowCount(result.total || 0);
      
      const reportsData = result.data; // The API now returns data in result.data
      if (Array.isArray(reportsData)) {
        setReports(reportsData);
      } else if (reportsData === null || reportsData === undefined) {
        // Handle null/empty data gracefully - show empty table
        setReports([]);
        setRowCount(0);
      } else {
        // Handle other unexpected formats by setting empty array
        setReports([]);
        setRowCount(0);
      }

    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchPaymentReports();
  }, [token, globalFilter, pagination, appliedFilters]);

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setAppliedFilters({
      payment_status: paymentStatusFilter.map((option) => option.value),
      invoice_status: invoiceStatusFilter.map((option) => option.value),
    });
  };

  const handleClearFilters = () => {
    setPaymentStatusFilter([]);
    setInvoiceStatusFilter([]);
    setSearchInput("");
    setGlobalFilter("");
    setPagination({ pageIndex: 0, pageSize: 10 });
    setAppliedFilters({ payment_status: [], invoice_status: [] });
  };

  const handleDeleteClick = (report: BatchPaymentReport) => {
    setDeleteTarget({
      id: report._id,
      name: report.batch_payment_id || "Batch Payment",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleCancelReport = async (report: BatchPaymentReport) => {
    const reportDocumentId = String(report.report_document_id || "").trim();
    if (!reportDocumentId) {
      showError("No report ID found.");
      return;
    }
    if (!token) {
      showError("Session expired. Please sign in again.");
      return;
    }
    if (cancelling) return;

    setCancelling(reportDocumentId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/reports/${encodeURIComponent(reportDocumentId)}/cancel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        let message = `Failed to cancel report (${response.status})`;
        try {
          const errBody = await response.json();
          message = String(errBody?.message || errBody?.error || message);
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      showSuccess("Invoice cancelled.");
      await fetchBatchPaymentReports();
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to cancel report.");
    } finally {
      setCancelling(null);
    }
  };

  // Handle delete report
  const handleDelete = async (reportId: string) => {
    setDeleting(reportId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/${userId}/payment-detail/${reportId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to delete batch payment";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      
      showSuccess("Batch payment deleted successfully!");
      
      setDeleting(null);
      
      // Refetch batch payment reports to get updated data
      await fetchBatchPaymentReports();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  // Table columns definition
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.bill_from || "", {
        id: "billFrom",
        header: () => <div className="th-text">Bill From</div>,
        cell: (info) => (
          <div className="text-sm">
            {info.getValue() === "N/A" ? "" : info.getValue()}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.bill_to || "", {
        id: "billTo",
        header: () => <div className="th-text">Bill To</div>,
        cell: (info) => (
          <div className="text-sm">
            {info.getValue() === "N/A" ? "" : info.getValue()}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => formatDate(row?.payment_date), {
        id: "paymentDate",
        header: () => <div className="th-text">Payment Date</div>,
        cell: (info) => (
          <div className="text-sm">
            {info.getValue() || "—"}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.payment_status || "", {
        id: "paymentStatus",
        header: () => <div className="th-text">Payment Status</div>,
        cell: (info) => {
          const paymentStatus = String(info.getValue() || "").trim();
          return (
            <div className="text-sm">
              {paymentStatus && paymentStatus !== "N/A" ? (
                <SalesOrderStatusBadge type="payment" value={paymentStatus} />
              ) : (
                "-"
              )}
            </div>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.invoice_status || "", {
        id: "invoiceStatus",
        header: () => <div className="th-text">Invoice Status</div>,
        cell: (info) => {
          const invoiceStatus = String(info.getValue() || "").trim();
          return (
            <div className="text-sm">
              {invoiceStatus && invoiceStatus !== "N/A" ? (
                <SalesOrderStatusBadge type="order" value={invoiceStatus} />
              ) : (
                "-"
              )}
            </div>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.date_range || "", {
        id: "dateRange",
        header: () => <div className="th-text">Date Range</div>,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="text-sm">
              {value === "N/A" ? "" : (
                <span className="px-2 py-2 br-8" style={{ backgroundColor: "#dbeafe", color: "#193cb8" }}>
                  {value}
                </span>
              )}
            </div>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.report_id || "", {
        id: "batchInvoiceId",
        header: () => <div className="th-text">Batch Invoice</div>,
        cell: (info) => {
          const reportId = String(info.getValue() || "").trim();
          const reportDocumentId = String(info.row.original.report_document_id || "").trim();
          if (!reportId) return <div className="text-sm">—</div>;
          if (!reportDocumentId) return <div className="text-sm order-link-1">{reportId}</div>;
          return (
            <Link
              to={`${baseRoute}/batchPaymentReport/${reportDocumentId}`}
              className="text-sm order-link-1"
            >
              {reportId}
            </Link>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => formatDate(row?.report_date || ""), {
        id: "reportDate",
        header: () => <div className="th-text">Report Date</div>,
        cell: (info) => (
          <div className="text-sm">
            {info.getValue() || "—"}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BatchPaymentReport) => row?.batch_payment_id || "", {
        id: "batchPaymentId",
        header: () => <div className="th-text">Payment Receipt</div>,
        cell: (info) => {
          const batchPaymentId = String(info.getValue() || "").trim();
          if (!batchPaymentId) return <div className="text-sm">—</div>;
          return (
            <Link
              to={`${baseRoute}/batchPaymentReceipt/${batchPaymentId}`}
              className="text-sm order-link-1"
            >
              {batchPaymentId}
            </Link>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      ...(showSignageBatchActions
        ? [
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const report = info.row.original;
          const reportDocumentId = String(report.report_document_id || "").trim();
          const viewTo = reportDocumentId
            ? `${baseRoute}/batchPaymentReport/${reportDocumentId}`
            : report.batch_payment_id
            ? `${baseRoute}/batchPaymentReceipt/${report.batch_payment_id}`
            : "";
          const isCancelled =
            String(report.invoice_status || "").trim().toLowerCase() === "cancelled";
          const amountAllowsDelete = Number(report.report_paid_amount ?? 0) <= 0;
          return (
            <div className="d-flex align-items-center gap-2">
              {!isCancelled && reportDocumentId ? (
                <CancelIconButton
                  title="Cancelled"
                  className="icon-square cancel"
                  onClick={() => handleCancelReport(report)}
                  disabled={cancelling === reportDocumentId}
                />
              ) : viewTo ? (
                canView && (
                  <ViewIconButton
                    to={viewTo}
                    className="btn action-btn"
                    title="View"
                  />
                )
              ) : null}
              {amountAllowsDelete && canDelete ? (
                <DeleteIconButton
                  title="Delete"
                  className="icon-square danger"
                  onClick={() => handleDeleteClick(report)}
                  disabled={deleting === report._id}
                />
              ) : null}
            </div>
          );
        },
      }),
        ]
        : []),
    ];
  }, [baseRoute, cancelling, deleting, pagination, canView, canDelete, showSignageBatchActions]);

  // Update export rows when reports change
  useEffect(() => {
    const rows: ExportRow[] = reports.map(report => ({
      "Batch Payment ID": report.batch_payment_id || '',
      "Batch Invoice ID": report.report_id || '',
      "Bill From": report.bill_from === "N/A" ? "" : report.bill_from || '',
      "Bill To": report.bill_to === "N/A" ? "" : report.bill_to || '',
      "Payment Date": formatDate(report.payment_date),
      "Payment Status": report.payment_status || '',
      "Invoice Status": report.invoice_status || '',
      "Report Date": formatDate(report.report_date || ''),
      "Date Range": report.date_range === "N/A" ? "" : report.date_range || '',
    }));

    setExportRows(rows);
  }, [reports]);

  // React table setup
  const table = useReactTable({
    data: reports,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  // Scroll to top on page change
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  return (
    <div className="content" ref={topRef}>
      <div className="d-flex justify-content-end align-items-center pb-3 mb-2 py-2">
        {/* <h4 className="mb-0">Batch Invoice</h4> */}
        <div className="d-flex gap-3 align-items-center">
          {showNewPaymentBatch && (
            <Link to={`${baseRoute}/batchPayment`} className="btn btn-primary table-addbutton">
            + New Payment Batch
          </Link>
          )}
        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Batch Invoice</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Bill From, Bill To, Batch Payment ID, Batch Invoice ID..."
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // ✅ Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="batch-payment-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            {/* <ExportAsExcel
              data={exportRows}
              headers={[
                "Report ID",
                "Bill From", 
                "Bill To",
                "Report Date",
                "Report Period",
                "Created By",
                "Created At"
              ]}
              fileName="BatchPaymentReports"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel> */}
          </div>
        </div>

        <div id="batch-payment-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Payment</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={PAYMENT_STATUS_OPTIONS}
                    value={paymentStatusFilter}
                    onChange={(options: MultiValue<FilterOption>) =>
                      setPaymentStatusFilter([...(options || [])])
                    }
                    isMulti
                    isSearchable
                    placeholder="Payment"
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={INVOICE_STATUS_OPTIONS}
                    value={invoiceStatusFilter}
                    onChange={(options: MultiValue<FilterOption>) =>
                      setInvoiceStatusFilter([...(options || [])])
                    }
                    isMulti
                    isSearchable
                    placeholder="Status"
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button
                  type="button"
                  className="btn btn-primary table-addbutton flex-fill flex-lg-grow-0"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-lg-grow-0"
                  onClick={handleClearFilters}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Batch Invoice</h5>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading batch payments…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading batch payments…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="batchPaymentId"
            titleColId="billFrom"
            actionColId="actions"
          />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
        emptyMessage="No batch payment reports found"
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Batch Payment"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default SignageBatchPayments;
